import { omit } from "lodash";

import type { SurveyAnswers } from "@/contexts";
import { calculateBMIImperial } from "@/utils/bmi";
import { omitMedicareNumber } from "@/utils/medicareNumber";

/** The options on the ACCESS disqualifying conditions question. */
export const ACCESS_EXCLUSION_OPTIONS = [
  "severeHeartCondition",
  "severeHighBloodPressure",
  "advancedKidneyDisease",
  "uncontrolledHyperthyroidism",
  "severeElectrolyteImbalance",
  "activeEatingDisorder",
  "pregnancy",
  "cognitiveImpairment",
  "severeMentalHealthCondition",
  "hospiceOrPalliativeCare",
  "nursingHome",
  // "None of these apply to me". It is not a condition.
  "none",
] as const;

export type AccessExclusionOption = (typeof ACCESS_EXCLUSION_OPTIONS)[number];

/** Narrows a survey answer to a list of ACCESS question options. */
export const isAccessExclusionOptions = (
  answer: unknown,
): answer is AccessExclusionOption[] =>
  Array.isArray(answer) &&
  answer.every((value) =>
    ACCESS_EXCLUSION_OPTIONS.some((option) => option === value),
  );

/** True if the member selected at least one disqualifying condition. */
export const hasAccessExclusion = (conditions?: AccessExclusionOption[]) =>
  !!conditions?.some((option) => option !== "none");

/**
 * The selected disqualifying conditions, stored in
 * `extras.accessDisqualifyingConditions`. "None" is not a condition, so an
 * eligible member gets an empty array.
 */
export const getAccessDisqualifyingConditions = (
  conditions: AccessExclusionOption[],
): AccessExclusionOption[] => conditions.filter((option) => option !== "none");

/** The options on the ACCESS qualifying conditions question. */
export const ACCESS_QUALIFYING_OPTIONS = [
  "highBloodPressure",
  "abnormalCholesterol",
  "prediabetes",
  "obesity",
  "overweight",
  // "None of these apply to me". It is not a condition.
  "none",
] as const;

export type AccessQualifyingOption = (typeof ACCESS_QUALIFYING_OPTIONS)[number];

/** Narrows a survey answer to a list of ACCESS qualifying question options. */
export const isAccessQualifyingOptions = (
  answer: unknown,
): answer is AccessQualifyingOption[] =>
  Array.isArray(answer) &&
  answer.every((value) =>
    ACCESS_QUALIFYING_OPTIONS.some((option) => option === value),
  );

/** The qualifying conditions the member selected ("None" is not a condition). */
export const getAccessQualifyingConditions = (
  conditions: AccessQualifyingOption[],
): AccessQualifyingOption[] => conditions.filter((option) => option !== "none");

/**
 * What the qualifying conditions answer means for ACCESS:
 * - QUALIFIED: high blood pressure (alone or with others), or at least two
 *   other conditions. The member continues to the disqualifying conditions.
 * - NEEDS_CONFIRMATION: exactly one condition that is not high blood pressure.
 *   The member is asked to confirm the selection and is not eligible if they do.
 * - NOT_QUALIFIED: "None of these apply to me" or nothing selected.
 */
export type AccessQualification =
  | "QUALIFIED"
  | "NEEDS_CONFIRMATION"
  | "NOT_QUALIFIED";

export const getAccessQualification = (
  conditions?: AccessQualifyingOption[],
): AccessQualification => {
  const selected = getAccessQualifyingConditions(conditions ?? []);

  if (selected.includes("highBloodPressure") || selected.length >= 2) {
    return "QUALIFIED";
  }

  return selected.length === 1 ? "NEEDS_CONFIRMATION" : "NOT_QUALIFIED";
};

/**
 * True once the member answered the qualifying conditions question and the
 * answer does not qualify them for ACCESS. A single condition that is not high
 * blood pressure counts as not qualified here: the member only reaches the next
 * step after confirming that selection.
 */
export const failedAccessQualification = (
  conditions?: AccessQualifyingOption[],
): boolean =>
  conditions !== undefined &&
  getAccessQualification(conditions) !== "QUALIFIED";

/** The allowed waist circumference range, in inches (inclusive). */
export const WAIST_CIRCUMFERENCE_MIN_INCHES = 15;
export const WAIST_CIRCUMFERENCE_MAX_INCHES = 120;

/**
 * True for the overweight BMI range (25 or more and below 30). Under ACCESS,
 * an overweight BMI alone is not enough to qualify, so these members are also
 * asked for their waist circumference.
 */
const isAccessOverweightBmi = (bmi: number): boolean => bmi >= 25 && bmi < 30;

/** True if the height and weight answer puts the member in the overweight range. */
export const shouldAskAccessWaistCircumference = (
  heightWeight?: SurveyAnswers["heightWeight"],
): boolean => {
  const { heightFeetPortion, heightInchesPortion, weight } = heightWeight ?? {};
  if (
    heightFeetPortion === undefined ||
    heightInchesPortion === undefined ||
    weight === undefined
  ) {
    return false;
  }
  return isAccessOverweightBmi(
    calculateBMIImperial(heightFeetPortion, heightInchesPortion, weight),
  );
};

/**
 * CMS ACCESS answers are PHI and are only persisted in `extras`
 * (`medicareBeneficiaryId`, `accessDisqualifyingConditions`,
 * `accessQualifyingConditions`, `waistCircumferenceInches`). Use this to drop
 * them from survey answers before they are sent anywhere else (account
 * creation `surveyAnswers`, secure storage triage data).
 */
export const omitCmsAccessPhi = (surveyAnswers: SurveyAnswers): SurveyAnswers =>
  omit(
    omitMedicareNumber(surveyAnswers),
    "cmsAccessDisqualifyingConditions",
    "cmsAccessQualifyingConditions",
    "waistCircumference",
  );
